// src/features/admin/careerApi.ts
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

// ── Types ──────────────────────────────────────────────────────────────────────

export interface Career {
  id: number;
  title: string;
  location?: string | null;
  type?: string | null;
  experience?: string | null;
  description?: string | null;
  is_active: number;
  created_at?: string;
}

export interface CareerFormData {
  title: string;
  location?: string;
  type?: string;
  experience?: string;
  description?: string;
  is_active?: number;
}

export interface Application {
  id: number;
  name: string;
  email: string;
  resume?: string | null;
  resume_url?: string | null;   // full URL returned by backend
  status: 'pending' | 'selected' | 'rejected';
  created_at?: string;
  career_id?: number;
  job_title?: string;
  job_type?: string;
}

export interface ApplyJobPayload {
  name: string;
  email: string;
  career_id: number;
  resume?: File | null;
}

export interface ApiResponse<T> {
  error: boolean;
  message: string;
  data?: T;
  total?: number;
}

// ── Base query WITHOUT global Content-Type ─────────────────────────────────────
// IMPORTANT: Never set Content-Type globally — file uploads need multipart/form-data
// which must be set automatically by the browser (with correct boundary).

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_SERVER_ENDPOINT,
  prepareHeaders: (headers) => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    headers.set(
      'authkey',
      '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3'
    );
    // ❌ DO NOT set Content-Type here — it breaks multipart/form-data uploads
    return headers;
  },
});

// ── API ────────────────────────────────────────────────────────────────────────

export const careerApi = createApi({
  reducerPath: 'careerApi',
  baseQuery,
  tagTypes: ['Career', 'Application'],
  endpoints: (build) => ({

    // ── Careers ──────────────────────────────────────────────────────────────

    listCareers: build.query<ApiResponse<Career[]>, void>({
      query: () => ({
        url: 'globalsettings/listCareers',
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }),
      providesTags: ['Career'],
    }),

    addCareer: build.mutation<ApiResponse<never> & { id?: number }, CareerFormData>({
      query: (body) => ({
        url: 'globalsettings/addCareer',
        method: 'POST',
        body,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['Career'],
    }),

    updateCareer: build.mutation<ApiResponse<never>, { id: number } & Partial<CareerFormData>>({
      query: ({ id, ...body }) => ({
        url: 'globalsettings/updateCareer',
        method: 'POST',
        body: { id, ...body },
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['Career'],
    }),

    deleteCareer: build.mutation<ApiResponse<never>, number>({
      query: (id) => ({
        url: 'globalsettings/deleteCareer',
        method: 'POST',
        body: { id },
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['Career'],
    }),

    // ── Applications ─────────────────────────────────────────────────────────

    listApplications: build.query<ApiResponse<Application[]>, void>({
      query: () => ({
        url: 'globalsettings/listApplications',
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }),
      providesTags: ['Application'],
    }),

    updateApplicationStatus: build.mutation<
      ApiResponse<never>,
      { id: number; status: 'pending' | 'selected' | 'rejected' }
    >({
      query: (body) => ({
        url: 'globalsettings/updateApplicationStatus',
        method: 'POST',
        body,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['Application'],
    }),

    deleteApplication: build.mutation<ApiResponse<never>, number>({
      query: (id) => ({
        url: 'globalsettings/deleteApplication',
        method: 'POST',
        body: { id },
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['Application'],
    }),

    // ── Apply Job (file upload — uses FormData, NO Content-Type header) ───────

    applyJob: build.mutation<ApiResponse<never> & { id?: number }, ApplyJobPayload>({
      query: ({ name, email, career_id, resume }) => {
        // Build FormData so multer receives multipart/form-data correctly
        const formData = new FormData();
        formData.append('name', name);
        formData.append('email', email);
        formData.append('career_id', String(career_id));
        if (resume) {
          formData.append('resume', resume); // actual File object
        }
        return {
          url: 'product/applyJob',
          method: 'POST',
          body: formData,
          // ❌ Do NOT set Content-Type — browser sets multipart/form-data + boundary automatically
        };
      },
      invalidatesTags: ['Application'],
    }),

  }),
});

export const {
  useListCareersQuery,
  useAddCareerMutation,
  useUpdateCareerMutation,
  useDeleteCareerMutation,
  useListApplicationsQuery,
  useUpdateApplicationStatusMutation,
  useDeleteApplicationMutation,
  useApplyJobMutation,
} = careerApi;